import _ from "lodash";
import React, {
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Detector } from "react-detect-offline";

import { useLatestValue, useEffectEvent } from "Hooks";
import { isIdEqual } from "Utils/idHelpers";
import { graphql, compose } from "lib/apolloClient";
import { prepareLoadMoreFeed } from "Utils";

import { getMessageV2UsersQuery } from "./MessagingQuery";
import {
  getMessageV2UsersFromCache,
  getChatConversationsFromCache,
} from "./MessagingGraphqlHelper";
import useMessagesData, {
  CURSOR_MESSAGE_TYPES,
  getQueryState,
  trimCachedMessagesData,
} from "./hooks/useMessagesData";
import {
  PAGINATION_LIMITS,
  getCursorMsgFromSearch,
  scrollToBottomInChat,
  scrollToMessageIfPresent,
  isPaidConnectModuleForStaff,
} from "./Utils";
import { STAFF, STUDENT } from "Constants/stringConstants";
import {
  getMessagingV2ReduxState,
  handleMessagesChanges,
  refetchChatMessagesDataInCache,
} from "./MessagingModule";
import {
  getOrganizationId,
  getUserCurriculumPrograms,
} from "modules/CommonSelectors";

export const getUserListEnhancer = ({
  userType,
  skip = () => false,
  selector = apiState => apiState,
  getVariables = () => ({}),
  includeTotalCount = false,
  firstFetchCount = 3,
  nextFetchCount = 10,
}) => {
  return graphql(getMessageV2UsersQuery, {
    name: "getMessageV2Users",
    skip: props => skip(props),
    options: props => {
      const {
        searchText,
        excludedUserIds,
        includePaidUsersOnly = false,
      } = props;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId: getOrganizationId(),
          portalType: "PLANNER",
          includeTotalCount,
          input: {
            first: firstFetchCount,
            userType: _.isArray(userType) ? userType : [userType],
            searchText,
            excludedUserIds: !_.isEmpty(excludedUserIds)
              ? excludedUserIds
              : undefined,
            includeInitiatedChatUsersOnlyForType: isPaidConnectModuleForStaff()
              ? []
              : [STAFF, STUDENT],
            includePaidUsersOnly,
            ...getVariables(props),
          },
        },
      };
    },
    props: ({ getMessageV2Users, ownProps }) => {
      const { isLoading, isData } = ownProps;

      const queryVariables = _.get(getMessageV2Users, "variables", {});
      const cachedData = getMessageV2UsersFromCache(
        getMessageV2Users.variables
      );

      const userNodeList = _.get(cachedData, "messageV2Users.edges", []);
      const hasNextPage = _.get(
        cachedData,
        "messageV2Users.pageInfo.hasNextPage",
        false
      );

      const totalCount = _.get(cachedData, "messageV2Users.totalCount", 0);
      const userParams = {
        userNodeList,
        hasNextPage,
        ...(includeTotalCount && { totalCount }),
        networkStatus: getMessageV2Users["networkStatus"],
        isFetchingMore: _.includes([3], getMessageV2Users["networkStatus"]),
        ...(includeTotalCount && { totalCount }),
        loadMoreUsers: prepareLoadMoreFeed({
          query: getMessageV2UsersQuery,
          variables: {
            ...queryVariables,
            input: {
              ..._.get(queryVariables, "input", {}),
              before: _.get(cachedData, "messageV2Users.pageInfo.endCursor"),
              first: nextFetchCount,
            },
          },
          queryData: getMessageV2Users,
          key: "messageV2Users",
        }),
      };

      return selector({
        isData: !_.isEmpty(cachedData) && isData,
        isLoading:
          _.includes([1, 2], getMessageV2Users["networkStatus"]) || isLoading,
        userParams,
      });
    },
  });
};

const getCursorState = ({ search, lastReadMessage }) => {
  const searchCursorMessage = getCursorMsgFromSearch(search);

  if (searchCursorMessage) {
    return {
      cursorMessageNode: searchCursorMessage,
      cursorMessageType: CURSOR_MESSAGE_TYPES.JUMP_TO_MESSAGE,
    };
  }

  return {
    cursorMessageNode: lastReadMessage,
    cursorMessageType: CURSOR_MESSAGE_TYPES.LAST_READ_MESSAGE,
  };
};

export const getBothWaysMessagesEnhancer = ({ skip = () => false } = {}) => {
  return WrappedComponent => {
    const BothWaysMessages = props => {
      const {
        chatId,
        lastReadMessage,
        unreadMessageCount,
        preservedScrollMessageId,
        handleMessagesChanges,
        location,
        ...rest
      } = props;

      const { search } = location;
      const preservedScrollMessageIdRef = useLatestValue(
        preservedScrollMessageId
      );
      const unreadMessageCountRef = useLatestValue(unreadMessageCount);

      // custom hook to get messages data
      const { getConversations, ...restHookProps } = useMessagesData({
        chatId,
      });

      /**
       * Desc: Managing cursor state, so that we can easily update with setState function
       * cursorState = { cursorMessageNode, cursorMessageType }
       * cursorMessageNode = { id, cursor } || null
       * cursorMessageType = one of CURSOR_MESSAGE_TYPES
       */
      const [cursorState, setCursorState] = useState(() =>
        getCursorState({
          search,
          lastReadMessage,
        })
      );

      // To track isLoading and isData state of the query
      const [queryState, setQueryState] = useState(() =>
        getQueryState({
          chatId,
          cursorState,
          unreadMessageCount,
        })
      );
      const [highlightedMessageId, setHighlightedMessageId] = useState(null);
      const timeoutRef = useRef();

      const highlightMessage = useCallback(messageId => {
        // clear previous pending timeout(if any) before setting new one
        clearTimeout(timeoutRef.current);
        setHighlightedMessageId(messageId);
        timeoutRef.current = setTimeout(() => {
          setHighlightedMessageId(null);
        }, 2000);
      }, []);

      // clear timeout on unmount/chatId change
      useEffect(() => () => clearTimeout(timeoutRef.current), [chatId]);

      const { isData, isLoading } = queryState;

      const updateCursorAndQueryState = useCallback(
        (nextCursorState, unreadMessageCount) => {
          const nextQueryState = getQueryState({
            chatId,
            unreadMessageCount,
            cursorState: nextCursorState,
          });
          setCursorState(nextCursorState);
          setQueryState(nextQueryState);
        },
        [chatId]
      );

      // This fires the actual query to get the data if not present
      useEffect(() => {
        const cleanUpRef = { isCleanedUp: false };
        const isJumpToMessage =
          cursorState.cursorMessageType ===
          CURSOR_MESSAGE_TYPES.JUMP_TO_MESSAGE;

        const nextQueryState = getQueryState({
          chatId,
          cursorState,
          unreadMessageCount: unreadMessageCountRef.current,
        });

        // _.isEqual will compare object values to avoid unnecessary re-renders
        setQueryState(prevState =>
          !_.isEqual(prevState, nextQueryState) ? nextQueryState : prevState
        );

        if (nextQueryState.isData) {
          // if we have data, just sync with cache on chat id change
          if (!isJumpToMessage) refetchChatMessagesDataInCache({ chatId });
          return;
        }

        if (nextQueryState.isMessagesSizeLimitReached) {
          const cursorMessageId = _.get(cursorState, "cursorMessageNode.id");

          trimCachedMessagesData({
            chatId,
            messageId:
              unreadMessageCountRef.current || isJumpToMessage
                ? cursorMessageId
                : preservedScrollMessageIdRef.current,
          });
          setQueryState({ isLoading: false, isData: true });
          refetchChatMessagesDataInCache({ chatId });
          return;
        }

        const getData = async () => {
          const isSuccess = await getConversations({
            chatId,
            cleanUpRef,
            cursorMessageNode: _.get(cursorState, "cursorMessageNode"),
          });
          if (cleanUpRef.isCleanedUp) return;
          setQueryState({ isLoading: false, isData: isSuccess });
        };

        getData();

        return () => {
          cleanUpRef.isCleanedUp = true;
        };
      }, [
        chatId,
        cursorState,
        unreadMessageCountRef,
        getConversations,
        preservedScrollMessageIdRef,
      ]);

      // This manages the logic to scroll to correct position once we get the data
      useLayoutEffect(() => {
        if (!isData || isLoading) return;
        const organizationId = getOrganizationId();
        const cachedData = getChatConversationsFromCache({
          id: chatId,
          organizationId,
        });
        const cursor = _.get(cursorState, "cursorMessageNode.cursor", null);
        const messageId = _.get(cursorState, "cursorMessageNode.id", null);
        const cursorMessageType = _.get(cursorState, "cursorMessageType");

        // if type is LAST_READ_MESSAGE and we don't have any unread messages,
        // scroll to the last preserved scroll position(if present) else scroll to bottom
        if (
          !unreadMessageCountRef.current &&
          cursorMessageType === CURSOR_MESSAGE_TYPES.LAST_READ_MESSAGE
        ) {
          if (preservedScrollMessageIdRef.current) {
            const isScrolled = scrollToMessageIfPresent({
              chatId,
              messageId: preservedScrollMessageIdRef.current,
              scrollOnlyIfNotVisible: false,
            });
            if (isScrolled) return;
          }

          scrollToBottomInChat({ chatId });
          return;
        }

        const cursorMessageEdge = _.find(
          _.get(cachedData, "messagesV2.edges", []),
          edge => isIdEqual(_.get(edge, "node.id"), messageId)
        );

        // if cursorMessageEdge not found, jump to bottom
        if (!cursor || !cursorMessageEdge) {
          scrollToBottomInChat({ chatId });
          return;
        }

        const isJumpToMessage =
          cursorMessageType === CURSOR_MESSAGE_TYPES.JUMP_TO_MESSAGE;

        scrollToMessageIfPresent({
          chatId,
          messageId,
          afterScrollCb: () => {
            if (isJumpToMessage) highlightMessage(messageId);
          },
        });
      }, [
        chatId,
        isData,
        isLoading,
        cursorState,
        highlightMessage,
        // following 2 are refs, will never change. Helpful to track latest values
        unreadMessageCountRef,
        preservedScrollMessageIdRef,
      ]);

      const checkForNewMessages = useEffectEvent(() => {
        // we are already fetching data, no need to fetch again
        if (isLoading) return;
        handleMessagesChanges({
          chatId,
          count: PAGINATION_LIMITS.MESSAGES_FEED,
        });
      });

      useEffect(() => {
        // helpful to fetch new messages when chatId changes to handle
        // any missed messages notifications
        checkForNewMessages();
      }, [chatId, checkForNewMessages]);

      // Explicit callback to initiate jump to message query
      const goToMessage = useCallback(
        newCursorMessageNode => {
          const nextCursorState = {
            cursorMessageNode: newCursorMessageNode,
            cursorMessageType: CURSOR_MESSAGE_TYPES.JUMP_TO_MESSAGE,
          };
          updateCursorAndQueryState(
            nextCursorState,
            unreadMessageCountRef.current
          );
        },
        [unreadMessageCountRef, updateCursorAndQueryState]
      );

      /* Following approach of updating state on change of props directly in functional
      component's body rather then using useEffect is much more performant and recommended.
      This is basically a way to implement getDerivedStateFromProps in functional components.
      Please refer React docs:
      1. https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
      2. https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
      */
      const [previousState, setPreviousState] = useState({ search, chatId });
      if (
        previousState.search !== search ||
        !isIdEqual(previousState.chatId, chatId)
      ) {
        const nextCursorState = getCursorState({ search, lastReadMessage });
        updateCursorAndQueryState(nextCursorState, unreadMessageCount);

        // reset highlighted message id if chatId changes
        if (!isIdEqual(previousState.chatId, chatId)) {
          setHighlightedMessageId(null);
        }
        setPreviousState({ search, chatId });
        return;
      }

      const handleNetworkChange = isOnline => {
        if (isOnline) {
          // check for any new messages
          checkForNewMessages();

          // refresh the data of already present messages
          trimCachedMessagesData({
            chatId,
            messageId: preservedScrollMessageId,
          });
          refetchChatMessagesDataInCache({ chatId });
        }
      };

      return (
        <>
          <Detector
            polling={false}
            render={() => null}
            onChange={handleNetworkChange}
          />
          <WrappedComponent
            {...rest}
            {...restHookProps}
            chatId={chatId}
            goToMessage={goToMessage}
            lastReadMessage={lastReadMessage}
            highlightedMessageId={highlightedMessageId}
            isLoading={_.get(props, "isLoading", false) || isLoading}
            isData={_.get(props, "isData", true) && isData}
            unreadMessageCount={unreadMessageCount}
          />
        </>
      );
    };

    const ComponentWrapper = props => {
      const skipScopeComparison = skip(props);
      return skipScopeComparison ? (
        <WrappedComponent {...props} />
      ) : (
        <BothWaysMessages {...props} />
      );
    };

    const mapStateToProps = (state, { chatId }) => ({
      preservedScrollMessageId: _.get(getMessagingV2ReduxState(state), [
        "preservedScrollMessageIds",
        chatId,
      ]),
    });

    const mapActions = { handleMessagesChanges };
    return compose(
      connect(mapStateToProps, mapActions),
      withRouter
    )(ComponentWrapper);
  };
};

/**
 * Creates an array of graphql enhancers — one per curriculum program —
 * each running the same query with the curriculum program's ID injected
 * into variables. Results are merged into `cohortParamsMap` compatible
 * with DualPanelManageMembersV2.
 *
 * Spread the returned array inside compose(...):
 *   compose(
 *     ...createCurriculumBasedQueryEnhancer({ ... }),
 *   )(Component)
 *
 * @param {Object}   config
 * @param {Object}   config.query             - GraphQL query document
 * @param {string}   config.namePrefix        - Unique prop-name prefix (e.g. "getCourseUsers")
 * @param {Function} config.skip              - (props, curriculum) => bool
 * @param {Function} config.getVariables      - (props, curriculum) => variables object
 * @param {Function} config.getFromCache      - (variables) => cached query data
 * @param {string}   config.edgesPath         - lodash path to edges array   (e.g. "courses.edges")
 * @param {string}   config.pageInfoPath      - lodash path to pageInfo      (e.g. "courses.pageInfo")
 * @param {Function} config.transformEdges    - (edges) => cohorts array for DualPanel
 * @param {string}   config.cohortType        - cohort type string (e.g. COHORT_TYPE.CLASSES)
 * @param {number}   config.indexStart        - starting index in cohortParamsMap (default 0)
 * @param {Function} config.buildHeaderParams - optional (curriculum) => headerParams object
 * @param {string}   config.loadMoreKey       - Apollo query key for prepareLoadMoreFeed
 * @returns {Array}  Array of graphql HOCs
 */
export const createCurriculumBasedQueryEnhancer = ({
  query,
  namePrefix,
  skip = () => false,
  getVariables,
  edgesPath,
  pageInfoPath,
  totalCountPath,
  transformEdges,
  cohortType,
  buildHeaderParams,
  loadMoreKey,
  buildLoadMoreVariables,
}) => {
  const curriculumPrograms = getUserCurriculumPrograms() || [];

  return _.map(curriculumPrograms, (curriculum, cohortIndex) => {
    const name = `${namePrefix}_${curriculum.id}`;

    return graphql(query, {
      name,
      skip: props => skip(props, curriculum),
      options: props => ({
        fetchPolicy: "cache-and-network",
        variables: getVariables(props, curriculum),
      }),
      props: ({ [name]: queryData, ownProps }) => {
        const { isLoading, isData, cohortParamsMap = {} } = ownProps;

        const queryVariables = _.get(queryData, "variables", {});
        const cachedData = _.get(queryData, "node", {});
        const edges = _.get(cachedData, edgesPath, []);
        const hasNextPage = _.get(
          cachedData,
          `${pageInfoPath}.hasNextPage`,
          false
        );
        const totalCount = totalCountPath
          ? _.get(cachedData, totalCountPath, 0)
          : undefined;
        const cohorts = transformEdges(edges, ownProps);

        return {
          isData: !_.isEmpty(cachedData) && isData,
          isLoading:
            _.includes([1, 2], queryData["networkStatus"]) || isLoading,
          cohortParamsMap: {
            ...cohortParamsMap,
            [cohortIndex]: {
              id: `${cohortType}_${curriculum.id}`,
              index: cohortIndex,
              type: cohortType,
              ...(buildHeaderParams && {
                headerParams: {
                  ...buildHeaderParams(curriculum),
                  ...(totalCountPath && { totalCount }),
                },
              }),
              cohorts,
              hasNextPage,
              networkStatus: queryData["networkStatus"],
              isFetchingMore: _.includes([3], queryData["networkStatus"]),
              loadMoreFeed: prepareLoadMoreFeed({
                query,
                variables: buildLoadMoreVariables
                  ? buildLoadMoreVariables(
                      queryVariables,
                      _.get(cachedData, `${pageInfoPath}.endCursor`)
                    )
                  : {
                      ...queryVariables,
                      after: _.get(cachedData, `${pageInfoPath}.endCursor`),
                    },
                queryData,
                key: loadMoreKey,
              }),
            },
          },
        };
      },
    });
  });
};
